import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { YOUTUBE_CONFIG } from '../config/constants';

function VideoComponent({ 
  videoState, 
  setVideoState, 
  favorites, 
  addToFavorites, 
  ytApiReady 
}) {
  // Handle video URL input change
  const handleVideoChange = (e) => {
    const url = e.target.value;
    
    // Check various YouTube URL formats
    const youtubeRegex = /(?:youtu\.be\/|youtube\.com\/(?:.*v=|.*\/|.*vi=|.*embed\/))([^&\n?#]+)/;
    const match = url.match(youtubeRegex);
    
    setVideoState(prev => ({
      ...prev,
      url: url,
      id: match && match[1] ? match[1] : null
    }));
  };

  // YouTube player initialization
  useEffect(() => {
    if (ytApiReady && videoState.id) {
      // Make sure the container element exists before trying to create the player
      const playerContainer = document.getElementById("youtube-player");
      if (!playerContainer) {
        // Create a container if it doesn't exist
        const container = document.createElement('div');
        container.id = 'youtube-player';
        container.className = 'video-frame';
        document.querySelector('.video-container').appendChild(container);
      }

      try {
        if (!videoState.player) {
          const newPlayer = new window.YT.Player("youtube-player", {
            videoId: videoState.id,
            height: YOUTUBE_CONFIG.PLAYER_HEIGHT,
            width: YOUTUBE_CONFIG.PLAYER_WIDTH,
            playerVars: YOUTUBE_CONFIG.PLAYER_VARS,
            events: {
              onReady: (event) => {
                console.log("YouTube player ready");
                event.target.setVolume(videoState.volume);
                if (event.target.getVideoData) {
                  setVideoState(prev => ({
                    ...prev,
                    title: event.target.getVideoData().title
                  }));
                }
              },
              onError: (event) => {
                console.error("YouTube player error", event);
              }
            }
          });
          setVideoState(prev => ({
            ...prev,
            player: newPlayer
          }));
        } else {
          videoState.player.loadVideoById(videoState.id);
          // Try to get video title
          setTimeout(() => {
            if (videoState.player.getVideoData) {
              setVideoState(prev => ({
                ...prev,
                title: videoState.player.getVideoData().title
              }));
            }
          }, YOUTUBE_CONFIG.TITLE_FETCH_DELAY);
        }
      } catch (error) {
        console.error("Error initializing YouTube player:", error);
      }
    }
  }, [videoState.id, ytApiReady, setVideoState]);

  // Update video volume when slider changes
  useEffect(() => {
    if (videoState.player && videoState.player.setVolume) {
      videoState.player.setVolume(videoState.volume);
    }
  }, [videoState.volume, videoState.player]);

  return (
    <div className="video-input" aria-labelledby="background-music-title">
      <h3 id="background-music-title" className="section-title">Background Music</h3>
      <div className="url-input-container">
        <label htmlFor="youtube-url-input" className="sr-only">YouTube URL</label>
        <input 
          id="youtube-url-input"
          type="text" 
          placeholder="Paste YouTube URL" 
          value={videoState.url} 
          onChange={handleVideoChange} 
          className="input" 
          aria-label="YouTube URL Input"
        />
        {videoState.id && (
          <button 
            onClick={addToFavorites} 
            className="action-button favorite-button"
            aria-label="Save to Favorites"
          >
            ★ Save
          </button>
        )}
      </div>
      
      {videoState.id && (
        <div className="video-container" aria-label="YouTube Video Player">
          <div id="youtube-player" className="video-frame"></div>
        </div>
      )}
    </div>
  );
}

VideoComponent.propTypes = {
  videoState: PropTypes.shape({
    url: PropTypes.string.isRequired,
    id: PropTypes.string,
    title: PropTypes.string,
    volume: PropTypes.number.isRequired,
    player: PropTypes.object
  }).isRequired,
  setVideoState: PropTypes.func.isRequired,
  favorites: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })).isRequired,
  addToFavorites: PropTypes.func.isRequired,
  ytApiReady: PropTypes.bool.isRequired
};

export default VideoComponent;